import React, { useState, Component } from 'react';
import { Search, Plus, Upload, X, Eye, Pencil, Tag as TagIcon, ListChecks, CircleDot, Hash, ListOrdered, AlignLeft, FileText } from 'lucide-react';
type RatingType = '5-point' | '10-point' | 'multiple-choice' | 'open-text';
type Question = {
  id: number;
  text: string;
  category: string;
  ratingType: RatingType;
  tags: string[];
  source: 'template' | 'custom';
  options?: string[];
};
const CATEGORIES = ['Leadership', 'Governance', 'Strategic Thinking', 'Accountability', 'Risk', 'Communication', 'Emotional Intelligence', 'Collaboration', 'Team Development'];
const RATING_TYPES: {
  id: RatingType;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
}[] = [{
  id: '5-point',
  label: '5-Point Scale',
  icon: CircleDot
}, {
  id: '10-point',
  label: '10-Point Scale',
  icon: Hash
}, {
  id: 'multiple-choice',
  label: 'Multiple Choice',
  icon: ListOrdered
}, {
  id: 'open-text',
  label: 'Open Text',
  icon: AlignLeft
}];
const RATING_LABEL: Record<RatingType, string> = {
  '5-point': '5-Point',
  '10-point': '10-Point',
  'multiple-choice': 'Multiple Choice',
  'open-text': 'Open Text'
};
const INITIAL_QUESTIONS: Question[] = [{
  id: 1,
  text: 'Sets a clear and compelling vision that aligns the team.',
  category: 'Strategic Thinking',
  ratingType: '5-point',
  tags: ['vision', 'alignment'],
  source: 'template'
}, {
  id: 2,
  text: 'Makes timely decisions even under conditions of uncertainty.',
  category: 'Leadership',
  ratingType: '5-point',
  tags: ['judgment'],
  source: 'template'
}, {
  id: 3,
  text: 'On a scale of 1–10, how effectively does this leader manage organizational risk?',
  category: 'Risk',
  ratingType: '10-point',
  tags: ['mitigation'],
  source: 'template'
}, {
  id: 4,
  text: "Which best describes this leader's communication style?",
  category: 'Communication',
  ratingType: 'multiple-choice',
  tags: ['clarity'],
  source: 'template',
  options: ['Direct & concise', 'Collaborative', 'Detailed & thorough', 'Inspirational']
}, {
  id: 5,
  text: 'What is one strength this leader should continue leveraging?',
  category: 'Emotional Intelligence',
  ratingType: 'open-text',
  tags: ['feedback', 'strengths'],
  source: 'custom'
}, {
  id: 6,
  text: 'Takes ownership of outcomes and follows through on commitments.',
  category: 'Accountability',
  ratingType: '5-point',
  tags: ['delivery', 'trust'],
  source: 'custom'
}, {
  id: 7,
  text: 'Builds productive partnerships across teams and departments.',
  category: 'Collaboration',
  ratingType: '5-point',
  tags: ['teamwork'],
  source: 'template'
}, {
  id: 8,
  text: 'Develops talent through coaching and growth opportunities.',
  category: 'Team Development',
  ratingType: '5-point',
  tags: ['growth', 'coaching'],
  source: 'template'
}];
function RatingPreview({
  type,
  options
}: {
  type: RatingType;
  options?: string[];
}) {
  if (type === '5-point') {
    return <div className="flex items-center justify-between" data-id="element-1936" data-mp="19lva2l8ajy7j">
        {[1, 2, 3, 4, 5].map(n => <div key={n} className="flex flex-col items-center gap-1" data-id="element-1937" data-mp="14m8w29kn2dxo">
            <div className="w-8 h-8 rounded-full border border-slate-300 bg-white flex items-center justify-center text-sm font-medium text-slate-600" data-id="element-1938" data-mp="gwxzz5e8h7pv">
              {n}
            </div>
          </div>)}
      </div>;
  }
  if (type === '10-point') {
    return <div className="flex items-center justify-between gap-1" data-id="element-1939" data-mp="qt14uj0gzkgi">
        {Array.from({
        length: 10
      }, (_, i) => i + 1).map(n => <div key={n} className="w-6 h-6 rounded-full border border-slate-300 bg-white flex items-center justify-center text-xs font-medium text-slate-600" data-id="element-1940" data-mp="228g0njgy06a1">
            {n}
          </div>)}
      </div>;
  }
  if (type === 'multiple-choice') {
    return <div className="space-y-2" data-id="element-1941" data-mp="btfbyo8a5f73">
        {(options && options.length ? options : ['Option A', 'Option B', 'Option C']).map((opt, i) => <label key={i} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-1942" data-mp="kun04dh30zng" data-mpi={i}>
            <span className="w-4 h-4 rounded-full border border-slate-300 inline-block" data-id="element-1943" data-mp="psoj6yeicyyx" />
            {opt}
          </label>)}
      </div>;
  }
  return <div className="w-full h-20 rounded-md border border-slate-300 bg-white p-2 text-sm text-slate-400" data-id="element-1944" data-mp="zihwq4y7no5t">
      Respondent's written answer…
    </div>;
}
export function QuestionLibrary() {
  const [questions] = useState<Question[]>(INITIAL_QUESTIONS);
  const [tab, setTab] = useState<'all' | 'template' | 'custom'>('all');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeRating, setActiveRating] = useState<RatingType | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Question | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [previewing, setPreviewing] = useState<Question | null>(null);
  const [draftType, setDraftType] = useState<RatingType>('5-point');
  const allTags = Array.from(new Set(questions.flatMap(q => q.tags))).sort();
  const filtered = questions.filter(q => {
    if (tab !== 'all' && q.source !== tab) return false;
    if (activeCategory && q.category !== activeCategory) return false;
    if (activeRating && q.ratingType !== activeRating) return false;
    if (activeTag && !q.tags.includes(activeTag)) return false;
    if (search && !q.text.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const openCreate = () => {
    setEditing(null);
    setDraftType('5-point');
    setDrawerOpen(true);
  };
  const openEdit = (q: Question) => {
    setEditing(q);
    setDraftType(q.ratingType);
    setDrawerOpen(true);
  };
  return <div className="animate-in fade-in duration-500" data-id="element-1945" data-mp="9mcxynuwqpqy">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-1946" data-mp="dnoe82rw5ymh">
        <div data-id="element-1947" data-mp="3vt1dln9xr3ye">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-1948" data-mp="3vshw27o76hkw">
            Question Library
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-1949" data-mp="4p2zyjtgd5ri">
            Manage reusable questions, templates, and custom items for your
            assessments.
          </p>
        </div>
        <div className="flex gap-3 shrink-0" data-id="element-1950" data-mp="3qw16ra59hx68">
          <button onClick={() => setImportOpen(true)} className="border border-slate-300 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors flex items-center" data-id="element-1951" data-mp="3qwt52sq8u907">
            <Upload className="h-4 w-4 mr-2" data-id="element-1952" data-mp="39kw0g96eoeej" />
            Bulk Import
          </button>
          <button onClick={openCreate} className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-1953" data-mp="35klvmzucnu19">
            <Plus className="h-4 w-4 mr-2" data-id="element-1954" data-mp="3ekidahibnsmt" />
            New Question
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-6 border-b border-slate-200 mb-6" data-id="element-1955" data-mp="3t83e24rq5frk">
        {([['all', 'All Questions'], ['template', 'Question Templates'], ['custom', 'Custom Questions']] as const).map(([key, label]) => <button key={key} onClick={() => setTab(key)} className={`text-sm font-medium pb-3 -mb-px border-b-2 transition-colors ${tab === key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-1956" data-mp="16x7lgeqd959">
            {label}
          </button>)}
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-1957" data-mp="1hzq6qxd9kjq3">
        {/* Sidebar */}
        <aside className="w-full lg:w-56 shrink-0 space-y-6" data-id="element-1958" data-mp="1m1be637145sw">
          <div data-id="element-1959" data-mp="1quw5jce4a1j4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 px-3" data-id="element-1960" data-mp="1quedkryhm9h0">
              Categories
            </h3>
            <nav className="space-y-0.5" data-id="element-1961" data-mp="z2or8im3317w">
              <button onClick={() => setActiveCategory(null)} className={`w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeCategory === null ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1962" data-mp="z3qavbh60w18">
                All Categories
              </button>
              {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${activeCategory === cat ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1963" data-mp="2jhgygha8qlos">
                  <span className="truncate" data-id="element-1964" data-mp="346rz7v3cafs">{cat}</span>
                  <span className="text-xs text-slate-400 ml-2" data-id="element-1965" data-mp="32nbph9fpze0">
                    {questions.filter(q => q.category === cat).length}
                  </span>
                </button>)}
            </nav>
          </div>

          <div className="border-t border-slate-200 pt-4" data-id="element-1966" data-mp="3la313lsmsxun">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 px-3" data-id="element-1967" data-mp="3l8i840lytmyw">
              Rating Type
            </h3>
            <nav className="space-y-0.5" data-id="element-1968" data-mp="3bc5bouz1cv0g">
              <button onClick={() => setActiveRating(null)} className={`w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeRating === null ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1969" data-mp="3bd6yfwpez5y0">
                All Types
              </button>
              {RATING_TYPES.map(rt => {
              const Icon = rt.icon;
              return <button key={rt.id} onClick={() => setActiveRating(rt.id)} className={`w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center ${activeRating === rt.id ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1970" data-mp="mtfv07ketc3e">
                    <Icon className="h-4 w-4 mr-2 text-slate-400" data-id="element-1971" data-mp="3lcwhfnfaa7p6" />
                    {rt.label}
                  </button>;
            })}
            </nav>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0 space-y-4" data-id="element-1972" data-mp="37jop1tjdp86f">
          <div className="space-y-3" data-id="element-1973" data-mp="37idl9tpxo0fb">
            <div className="relative" data-id="element-1974" data-mp="37hbwynjwphfn">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-1975" data-mp="32lddnedfqpm3" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search questions..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-1976" data-mp="1i2t25rjfagbj" />
            </div>
            <div className="flex flex-wrap items-center gap-2" data-id="element-1977" data-mp="20taiewn2jjg0">
              <span className="text-xs font-medium text-slate-400 flex items-center mr-1" data-id="element-1978" data-mp="20ttz6a0zg1sg">
                <TagIcon className="h-3.5 w-3.5 mr-1" data-id="element-1979" data-mp="25rcdkfxrr97u" /> Tags:
              </span>
              {allTags.map(tag => <button key={tag} onClick={() => setActiveTag(activeTag === tag ? null : tag)} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${activeTag === tag ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-1980" data-mp="1rup2zacw8kjg">
                  {tag}
                </button>)}
            </div>
          </div>

          {filtered.length === 0 ? <div className="border border-dashed border-slate-300 rounded-xl py-16 text-center bg-white" data-id="element-1981" data-mp="2dbd8er65rp9g">
              <div className="mx-auto w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-4" data-id="element-1982" data-mp="28d3d8gkfoonu">
                <ListChecks className="h-6 w-6 text-slate-400" data-id="element-1983" data-mp="24bzw7qqngml1" />
              </div>
              <h3 className="text-sm font-medium text-slate-900" data-id="element-1984" data-mp="1zbc5z7a8w9rv">
                No questions found
              </h3>
              <p className="text-sm text-slate-500 mt-1 mb-4" data-id="element-1985" data-mp="1udtupa8r9igp">
                Try adjusting your filters or create a new question.
              </p>
              <button onClick={openCreate} className="text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50 inline-flex items-center" data-id="element-1986" data-mp="1pg254mvl28fo">
                <Plus className="h-4 w-4 mr-1.5" data-id="element-1987" data-mp="3s994crm9m4vr" /> New Question
              </button>
            </div> : <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100 overflow-hidden" data-id="element-1988" data-mp="3vhd8zbjx9u">
              {filtered.map(q => <div key={q.id} className="p-4 flex items-start gap-4 hover:bg-slate-50 transition-colors group" data-id="element-1989" data-mp="9ugltd1obw0o">
                  <div className="flex-1 min-w-0" data-id="element-1990" data-mp="39by5v98gjfhd">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-1991" data-mp="3daoust2rrqte">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600" data-id="element-1992" data-mp="3iat9qeo14dhi">
                        {q.category}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700" data-id="element-1993" data-mp="xku62he0oqkp">
                        {RATING_LABEL[q.ratingType]}
                      </span>
                      {q.source === 'custom' && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700" data-id="element-1994" data-mp="2bi6vvh84v28e">
                          Custom
                        </span>}
                    </div>
                    <p className="text-sm font-medium text-slate-900" data-id="element-1995" data-mp="2fgw650xvk3nt">
                      {q.text}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-1996" data-mp="2uddinvhsvoby">
                      {q.tags.map(t => <span key={t} className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-500" data-id="element-1997" data-mp="2pe0v18j3ud8b">
                          {t}
                        </span>)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-id="element-1998" data-mp="386uxx43geew6">
                    <button onClick={() => setPreviewing(q)} title="Preview" className="p-1.5 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100" data-id="element-1999" data-mp="3rtg3dapyqtkr">
                      <Eye className="h-4 w-4" data-id="element-2000" data-mp="3hwl4urtowt6h" />
                    </button>
                    <button onClick={() => openEdit(q)} title="Edit" className="p-1.5 text-slate-400 hover:text-slate-900 rounded-md hover:bg-slate-100" data-id="element-2001" data-mp="3mrgzlzqb9una">
                      <Pencil className="h-4 w-4" data-id="element-2002" data-mp="2z3zg0rh7a2ve" />
                    </button>
                  </div>
                </div>)}
            </div>}
        </div>
      </div>

      {/* Create / Edit Drawer with live preview */}
      {drawerOpen && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-2003" data-mp="2q55kl4flvijf">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setDrawerOpen(false)} data-id="element-2004" data-mp="2u3crl7k9p5hf" />
          <div className="relative w-full max-w-lg bg-white h-full shadow-xl flex flex-col animate-in slide-in-from-right duration-300" data-id="element-2005" data-mp="vv7815dzu1dm">
            <div className="flex items-center justify-between px-6 h-16 border-b border-slate-200" data-id="element-2006" data-mp="qx5okjcno01m">
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-2007" data-mp="ly2geq1dpdpf">
                {editing ? 'Edit Question' : 'New Question'}
              </h2>
              <button onClick={() => setDrawerOpen(false)} className="text-slate-400 hover:text-slate-600" data-id="element-2008" data-mp="1jif9tdc7qsaw">
                <X className="h-5 w-5" data-id="element-2009" data-mp="19lamvbajmst1" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-id="element-2010" data-mp="15ls2qhqjqlr9">
              <div data-id="element-2011" data-mp="3ucum91ilw4pa">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2012" data-mp="3udcfrqdvw4ti">
                  Question Text *
                </label>
                <textarea rows={2} defaultValue={editing?.text || ''} placeholder="Enter the question..." className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2013" data-mp="3q9o8l8t5zb8s" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-2014" data-mp="2jl4wmhxxgszn">
                <div data-id="element-2015" data-mp="2jmohymyfxu8a">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2016" data-mp="2ojokinw30eun">
                    Category *
                  </label>
                  <select defaultValue={editing?.category || ''} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2017" data-mp="2thgedngaeb1h">
                    <option value="" disabled data-id="element-2018" data-mp="13753pqxi23lw">
                      Select…
                    </option>
                    {CATEGORIES.map(cat => <option key={cat} value={cat} data-id="element-2019" data-mp="1879ldka594dp">
                        {cat}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-2020" data-mp="36150c6193d7g">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-2021" data-mp="35zkd71h9mwjm">
                    Tags
                  </label>
                  <input type="text" defaultValue={editing?.tags.join(', ') || ''} placeholder="comma, separated" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-2022" data-mp="3etfbpqnvtoqa" />
                </div>
              </div>
              <div data-id="element-2023" data-mp="2w2xtwh4l8djp">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2024" data-mp="2w1mh5rt5zvgg">
                  Rating Type *
                </label>
                <div className="grid grid-cols-2 gap-2" data-id="element-2025" data-mp="gdw5ooo7b6kp">
                  {RATING_TYPES.map(rt => {
                const Icon = rt.icon;
                const selected = draftType === rt.id;
                return <button key={rt.id} onClick={() => setDraftType(rt.id)} className={`flex items-center p-3 border rounded-lg text-sm font-medium transition-all ${selected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-2026" data-mp="1y9y8vh3cuzs1">
                        <Icon className={`h-4 w-4 mr-2 ${selected ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-2027" data-mp="2gzxv7w7ta0rr" />
                        {rt.label}
                      </button>;
              })}
                </div>
              </div>
              {/* Live Preview */}
              <div className="border-t border-slate-100 pt-5" data-id="element-2028" data-mp="2vwok3bjuzu34">
                <div className="flex items-center text-sm font-medium text-slate-700 mb-3" data-id="element-2029" data-mp="30nwwmgghjvki">
                  <Eye className="h-4 w-4 mr-2 text-slate-400" data-id="element-2030" data-mp="3gxxpgu7th1nv" /> Preview
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-id="element-2031" data-mp="3lwr2klj6yqni">
                  <p className="text-sm font-medium text-slate-900 mb-4" data-id="element-2032" data-mp="3lylfbckb7cgi">
                    {editing?.text || 'Your question will appear here.'}
                  </p>
                  <RatingPreview type={draftType} options={editing?.options} data-id="element-2033" data-mp="3c5ebhw57w4ij" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3" data-id="element-2034" data-mp="2y9iw40a11bjw">
              <button onClick={() => setDrawerOpen(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white transition-colors" data-id="element-2035" data-mp="2p6htwaoa84li">
                Cancel
              </button>
              <button onClick={() => setDrawerOpen(false)} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-2036" data-mp="nd0oldnmjftx">
                {editing ? 'Save Changes' : 'Create Question'}
              </button>
            </div>
          </div>
        </div>}

      {/* Preview Modal */}
      {previewing && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2037" data-mp="2lqf0whwlrdgn">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setPreviewing(null)} data-id="element-2038" data-mp="2gwj0z9ws3giu" />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl animate-in zoom-in-95 duration-200" data-id="element-2039" data-mp="2qoggutyqewnw">
            <div className="flex items-center justify-between px-6 h-14 border-b border-slate-200" data-id="element-2040" data-mp="34m52w54jc9uj">
              <h2 className="text-base font-semibold text-slate-900" data-id="element-2041" data-mp="2zpn1nf8eytmy">
                Question Preview
              </h2>
              <button onClick={() => setPreviewing(null)} className="text-slate-400 hover:text-slate-600" data-id="element-2042" data-mp="1a9nl3d2gu1xa">
                <X className="h-5 w-5" data-id="element-2043" data-mp="35dd9x2imswox" />
              </button>
            </div>
            <div className="p-6" data-id="element-2044" data-mp="3aber1hwhylxr">
              <div className="flex items-center gap-2 mb-3" data-id="element-2045" data-mp="3acpxjphbu7sk">
                <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-600" data-id="element-2046" data-mp="3fazrjwrbf2cl">
                  {previewing.category}
                </span>
                <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700" data-id="element-2047" data-mp="3ja07n7ierqvw">
                  {RATING_LABEL[previewing.ratingType]}
                </span>
              </div>
              <p className="text-sm font-medium text-slate-900 mb-5" data-id="element-2048" data-mp="1w3giremr31t">
                {previewing.text}
              </p>
              <RatingPreview type={previewing.ratingType} options={previewing.options} data-id="element-2049" data-mp="6v7ppi1k10s5" />
            </div>
          </div>
        </div>}

      {/* Bulk Import Modal */}
      {importOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2050" data-mp="ftjmy2wxewtk">
          <div className="absolute inset-0 bg-slate-900/30 animate-in fade-in" onClick={() => setImportOpen(false)} data-id="element-2051" data-mp="20f87b0fcmq14" />
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl animate-in zoom-in-95 duration-200" data-id="element-2052" data-mp="1rcpb2z62jbnu">
            <div className="flex items-center justify-between px-6 h-14 border-b border-slate-200" data-id="element-2053" data-mp="1menmjypvik4z">
              <h2 className="text-base font-semibold text-slate-900" data-id="element-2054" data-mp="2j33g05w3tdnd">
                Bulk Import Questions
              </h2>
              <button onClick={() => setImportOpen(false)} className="text-slate-400 hover:text-slate-600" data-id="element-2055" data-mp="2f32zmfpg2sdo">
                <X className="h-5 w-5" data-id="element-2056" data-mp="25d9thu82n5gt" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-2057" data-mp="32s5ifqvter9r">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center" data-id="element-2058" data-mp="32sxf7515ev1m">
                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-3" data-id="element-2059" data-mp="2y0ngp0lywmnq" />
                <p className="text-sm font-medium text-slate-900" data-id="element-2060" data-mp="2hlej429zjckc">
                  Drag & drop your CSV file
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-2061" data-mp="2mmkgidelbo07">
                  or click to browse
                </p>
                <button className="mt-4 text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50" data-id="element-2062" data-mp="28n2prshqi0o7">
                  Choose File
                </button>
              </div>
              <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3" data-id="element-2063" data-mp="31c9l6ilw6a58">
                <FileText className="h-4 w-4 shrink-0 mt-0.5" data-id="element-2064" data-mp="2rklzkyc0nnbo" />
                <span data-id="element-2065" data-mp="2ri9j6plwpl0q">
                  CSV columns:{' '}
                  <strong data-id="element-2066" data-mp="2whvw0xxtoz90">question, category, rating_type, tags</strong>. Rating
                  type must be one of 5-point, 10-point, multiple-choice,
                  open-text.{' '}
                  <a href="#" className="text-slate-900 underline" data-id="element-2067" data-mp="3p0jcu15sds4a">
                    Download template
                  </a>
                </span>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3" data-id="element-2068" data-mp="3ud7vw29io79i">
              <button onClick={() => setImportOpen(false)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white transition-colors" data-id="element-2069" data-mp="byyq7pzpxwll">
                Cancel
              </button>
              <button onClick={() => setImportOpen(false)} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-2070" data-mp="3be3rx7wu13op">
                Import
              </button>
            </div>
          </div>
        </div>}
    </div>;
}